<script setup lang="ts">
import { computed } from 'vue'
import { Loader2Icon } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'warning'   // 경고 스타일 (주황)
  | 'danger'    // 위험 스타일 (빨강)

type LabelPosition =
  | 'right'     // 오른쪽 (기본)
  | 'left'      // 왼쪽
  | 'top'       // 위쪽
  | 'bottom'    // 아래쪽

const props = withDefaults(
  defineProps<{
    size?: 'xs' | 'sm' | 'md' | 'lg'
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 스피너 원의 테두리 두께 */
    thickness?: number
    /** 스피너 옆에 표시될 텍스트 */
    label?: string
    /** 라벨 위치 */
    labelPosition?: LabelPosition
  }>(),
  {
    size: 'sm',
    styletype: 'default',
    thickness: 2,
    label: '',
    labelPosition: 'right',
  },
)

/**
 * 크기별 클래스 매핑
 */
const SIZE_CLASSES = {
  xs: 'size-2.5',
  sm: 'size-3',
  md: 'size-4', 
  lg: 'size-5',
}

/**
 * 라벨 위치별 레이아웃 클래스 매핑
 */
const LABEL_POSITION_CLASSES = {
  right: 'flex-row items-center gap-2',
  left: 'flex-row-reverse items-center gap-2',
  top: 'flex-col items-center gap-2',
  bottom: 'flex-col-reverse items-center gap-2',
}

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  primary: { 
    class: 'text-blue-500',
  },
  success: { 
    class: 'text-green-500',
  },
  warning: { 
    class: 'text-amber-500',
  },
  danger: { 
    class: 'text-red-500',
  },
}

/** 최종 바인딩: 직접 넘긴 props가 있으면 styletype 기본값과 병합 */
const spinnerProps = computed(() => {
  const preset = STYLE_PRESETS[props.styletype!]
  const finalSize = props.size || 'md'
  const sizeClass = SIZE_CLASSES[finalSize as keyof typeof SIZE_CLASSES]
  const finalClass = cn('animate-spin', sizeClass, preset.class, props.class)
  
  return {
    class: finalClass,
    style: {
      strokeWidth: props.thickness,
    },
    'aria-label': '처리 중',
  }
})

/** 컨테이너 레이아웃 클래스 */
const containerClass = computed(() => {
  const position = props.labelPosition || 'right'
  return cn('inline-flex', LABEL_POSITION_CLASSES[position as keyof typeof LABEL_POSITION_CLASSES])
})
</script>

<template>
  <div :class="containerClass">
    <Loader2Icon
      role="status"
      v-bind="spinnerProps"
    />
    <span v-if="props.label" class="text-sm">{{ props.label }}</span>
  </div>
</template>
